import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '../lib/AuthContext';
import { b2bApi } from '../api/client';
import { Check } from 'lucide-react';

const PROFESSIONS = ['apotheker', 'dokter', 'therapeut', 'kinesist', 'verpleegkundige', 'anders'];
const PROFESSION_LABELS = {
  apotheker: 'Apotheker', dokter: 'Dokter', therapeut: 'Therapeut',
  kinesist: 'Kinesist', verpleegkundige: 'Verpleegkundige', anders: 'Anders',
};

const inputClass = 'w-full px-3.5 py-2.5 rounded-lg border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring transition-shadow';

function Field({ label, hint, error, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-foreground mb-1.5">
        {label}{hint && <span className="ml-1.5 text-xs text-muted-foreground font-normal">{hint}</span>}
      </label>
      {children}
      {error && <p className="text-xs text-destructive mt-1">Dit veld is verplicht</p>}
    </div>
  );
}

export default function RegisterB2B() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const { register, handleSubmit, watch, formState: { errors } } = useForm({
    defaultValues: { addressCountry: 'BE' },
  });
  const profession = watch('profession');

  if (!user) {
    navigate('/login');
    return null;
  }

  const onSubmit = async (data) => {
    setLoading(true);
    setError('');
    try {
      await b2bApi.register(data);
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Er is een fout opgetreden');
    } finally {
      setLoading(false);
    }
  };

  if (success) return (
    <div className="min-h-[70vh] flex items-center justify-center px-6" data-testid="b2b-success">
      <div className="text-center max-w-sm">
        <div className="w-14 h-14 rounded-full bg-primary flex items-center justify-center mx-auto mb-5">
          <Check className="w-7 h-7 text-primary-foreground" />
        </div>
        <h2 className="font-serif text-3xl font-light mb-3 text-foreground">Aanvraag ingediend</h2>
        <p className="text-muted-foreground text-sm mb-6 leading-relaxed">
          Uw B2B-aanvraag is ontvangen. Ons team beoordeelt uw profiel en neemt contact op zodra uw account is goedgekeurd.
        </p>
        <button
          data-testid="back-to-shop"
          onClick={() => navigate('/')}
          className="text-sm text-primary font-medium hover:underline"
        >
          Terug naar de winkel
        </button>
      </div>
    </div>
  );

  return (
    <div className="max-w-xl mx-auto px-6 py-12" data-testid="b2b-registration-page">
      <div className="mb-8">
        <h1 className="font-serif text-4xl font-light mb-2 text-foreground">B2B Registratie</h1>
        <p className="text-muted-foreground text-sm">Registreer uw bedrijf en professioneel profiel</p>
        <div className="w-10 h-px bg-accent mt-4" />
      </div>

      {error && <div data-testid="b2b-error" className="text-sm text-destructive bg-destructive/10 px-4 py-2.5 rounded-lg mb-6">{error}</div>}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Personal */}
        <div className="bg-card border border-border rounded-2xl p-6 space-y-4 shadow-sm">
          <h2 className="font-serif text-base font-medium text-foreground">Persoonlijke gegevens</h2>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Voornaam" error={errors.firstName}>
              <input data-testid="b2b-first-name" {...register('firstName', { required: true })} className={inputClass} />
            </Field>
            <Field label="Achternaam" error={errors.lastName}>
              <input data-testid="b2b-last-name" {...register('lastName', { required: true })} className={inputClass} />
            </Field>
          </div>
          <Field label="Telefoon" hint="(optioneel)">
            <input type="tel" {...register('phone')} className={inputClass} placeholder="+32 ..." />
          </Field>
        </div>

        {/* Company */}
        <div className="bg-card border border-border rounded-2xl p-6 space-y-4 shadow-sm">
          <h2 className="font-serif text-base font-medium text-foreground">Bedrijfsgegevens</h2>
          <Field label="Bedrijfsnaam" error={errors.companyName}>
            <input data-testid="b2b-company" {...register('companyName', { required: true })} className={inputClass} />
          </Field>
          <Field label="BTW-nummer" hint="(optioneel)">
            <input data-testid="b2b-vat" {...register('vatNumber')} className={inputClass} placeholder="BE 0000.000.000" />
          </Field>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <Field label="Straat" error={errors.addressStreet}>
                <input {...register('addressStreet', { required: true })} className={inputClass} />
              </Field>
            </div>
            <Field label="Huisnr." error={errors.addressNumber}>
              <input {...register('addressNumber', { required: true })} className={inputClass} />
            </Field>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Bus" hint="(opt.)">
              <input {...register('addressBus')} className={inputClass} />
            </Field>
            <Field label="Postcode" error={errors.addressPostalCode}>
              <input {...register('addressPostalCode', { required: true })} className={inputClass} />
            </Field>
            <Field label="Stad" error={errors.addressCity}>
              <input {...register('addressCity', { required: true })} className={inputClass} />
            </Field>
          </div>
          <div className="max-w-[160px]">
            <Field label="Land">
              <input {...register('addressCountry')} className={inputClass} />
            </Field>
          </div>
        </div>

        {/* Profession */}
        <div className="bg-card border border-border rounded-2xl p-6 space-y-4 shadow-sm">
          <h2 className="font-serif text-base font-medium text-foreground">Beroep</h2>
          <Field label="Beroep" error={errors.profession}>
            <select data-testid="b2b-profession" {...register('profession', { required: true })} className={inputClass} defaultValue="">
              <option value="" disabled>-- kies uw beroep --</option>
              {PROFESSIONS.map(p => <option key={p} value={p}>{PROFESSION_LABELS[p]}</option>)}
            </select>
          </Field>
          {profession === 'anders' && (
            <Field label="Omschrijving beroep" hint="(optioneel)">
              <input {...register('professionOther')} className={inputClass} />
            </Field>
          )}
        </div>

        <button
          data-testid="submit-b2b"
          type="submit" disabled={loading}
          className="w-full bg-primary text-primary-foreground rounded-full py-3.5 font-semibold hover:bg-primary/90 disabled:opacity-50 transition-all active:scale-95"
        >
          {loading ? 'Aanvraag indienen...' : 'Aanvraag indienen'}
        </button>
      </form>
    </div>
  );
}
